import React, { useId, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { bandChipClass, admitRatePercent } from '../../utils/fitBands';

// ============================================================================
// IN-THREAD SCHOOL PICKER (#1188)
//
// The concierge used to list school NAMES as prose with one "Add <name>" chip
// each: the student was asked to choose with no basis and could only take one
// at a time. Founder 2026-08-03 — "it should show the odds and category (Reach,
// Target or Safety)" and "it should allow the user to do multi select". So the
// scored candidates the turn already read are rendered as rows carrying the
// band and the school's admit rate, selectable together. (The numeric match
// was retired from every student surface, founder 2026-08-08 — the band IS
// the odds verdict.)
//
// Rules:
//   - the payload is untrusted-shaped: anything malformed renders nothing
//     rather than throwing inside the transcript
//   - NOTHING is pre-selected — the list is the student's
//   - a missing number reads as MISSING. "—" with a spoken "not available",
//     never a 0% that looks like a real, terrible number
//   - this component adds nothing. It sends ONE chat turn naming the picks and
//     carrying their ids (#1197); the backend proposes a single confirmation
//     card and THAT card is the consent surface (ADR 0004). No API call here.
//   - #1199: the server ships more rows than it asks us to show. "Show me more"
//     reveals rows that are ALREADY on the wire — no turn, no lookup, no wait.
// ============================================================================

const DEFAULT_MAX = 5;
const DEFAULT_VISIBLE = 8;

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

// The server sends an ordered, pre-capped list — respect that order (it is the
// counselor's reasoning for THIS turn, not a generic ranking).
const normalizeSchools = (raw) => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    const out = [];
    for (const s of raw) {
        const name = str(s?.name);
        if (!name) continue;
        // The id is what the batch tool resolves against; the name is the only
        // thing the student ever sees, so a row without one is unusable either
        // way. Dedupe on whichever identifies the school.
        const id = str(s.university_id) || name;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({
            id,
            name,
            band: str(s.band),
            admit: admitRatePercent(s.acceptance_rate),
            state: str(s.state),
            majorNote: str(s.major_note),
            majorImpacted: s.major_impacted === true,
        });
    }
    return out;
};

export default function SchoolPicker({ picker, onSend, busy }) {
    const all = useMemo(() => normalizeSchools(picker?.schools), [picker]);
    const max = Number.isFinite(picker?.max) && picker.max > 0
        ? Math.floor(picker.max) : DEFAULT_MAX;
    // How many the server asks us to show first. The rest are real rows from
    // the same read, held back so eight schools don't bury the reply.
    const visible = Number.isFinite(picker?.visible) && picker.visible > 0
        ? Math.floor(picker.visible) : DEFAULT_VISIBLE;
    const uid = useId();

    const [selected, setSelected] = useState([]);   // university ids, in tap order
    const [expanded, setExpanded] = useState(false);

    // Each turn replaces `picker` wholesale, exactly like the suggestion chips
    // — so a new one drops any in-progress selection. Adjusting state during
    // render (React's documented pattern for props-changed resets) keeps the
    // reset in the SAME commit as the new rows; an effect here would paint one
    // frame of the previous turn's selection against this turn's schools.
    const [seenPicker, setSeenPicker] = useState(picker);
    if (picker !== seenPicker) {
        setSeenPicker(picker);
        setSelected([]);
        setExpanded(false);
    }

    if (all.length === 0) return null;

    // Derived during render, never seeded from an effect — the same rule the
    // selection reset above follows.
    const schools = expanded ? all : all.slice(0, visible);
    const hidden = all.length - schools.length;

    const toggle = (id) => {
        setSelected((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= max) return prev;
            return [...prev, id];
        });
    };

    // List order, not tap order: the message reads as the counselor's own
    // shortlist rather than a record of which row was touched first. Taken
    // from ALL rows, not the visible slice, so a pick can never be dropped
    // by what happens to be on screen.
    const picks = all.filter((s) => selected.includes(s.id));
    const count = picks.length;

    const submit = () => {
        if (count === 0 || busy) return;
        // The student sees a plain sentence; the IDS travel as data (#1197).
        // Before this the ids had to survive a round-trip through the model,
        // which slugified "University of California, Davis" into
        // 'university-of-california-davis' and the add failed outright. The
        // server proposes the card straight from these ids — no model call,
        // no transcription — and that card is still the only consent.
        onSend?.(`Add these to my list: ${picks.map((s) => s.name).join(', ')}`,
                 { selectedSchoolIds: picks.map((s) => s.id) });
    };

    return (
        <div data-testid="concierge-school-picker"
             className="mb-2 rounded-xl border border-[#D6E8D5] bg-[#FAFCFA] p-2.5">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#9A968C]"
                      data-testid="concierge-school-budget">
                    Your picks · {count} of {max}
                </span>
                <span className="text-[10px] text-[#9A968C]">Tap to choose</span>
            </div>

            {/* Capped height: this sits above the composer, and eight rows must
                not push the input off a phone screen. */}
            <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto pr-0.5">
                {schools.map((school) => {
                    const isSelected = selected.includes(school.id);
                    const atCap = !isSelected && count >= max;
                    const chip = bandChipClass(school.band);
                    const detailId = `${uid}-${school.id}`;
                    // The full sentence a screen reader gets. Every unknown is
                    // spoken as unknown — this is the honesty requirement, and
                    // the compact visual row below is aria-hidden precisely so
                    // "—" is never what gets announced.
                    const detail = [
                        school.band ? `${school.band}.` : null,
                        `Acceptance rate ${school.admit != null ? `${school.admit}%` : 'not available'}.`,
                        school.state ? `In ${school.state}.` : null,
                        school.majorNote ? `${school.majorNote}.` : null,
                    ].filter(Boolean).join(' ');
                    return (
                        <li key={school.id} data-testid="concierge-picker-school">
                            {/* The whole row is the control — a bare name gives
                                no hint it is tappable. */}
                            <div
                                role="button"
                                tabIndex={0}
                                aria-pressed={isSelected}
                                aria-label={`Select ${school.name}`}
                                aria-describedby={detailId}
                                aria-disabled={atCap || undefined}
                                onClick={() => !atCap && toggle(school.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        if (!atCap) toggle(school.id);
                                    }
                                }}
                                className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5
                                    transition-colors focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/40
                                    ${isSelected
                                        ? 'border-[#1A4D2E] bg-[#F0F7F1]'
                                        : atCap
                                            ? 'cursor-not-allowed border-[#EFEDE7] bg-white opacity-50'
                                            : 'cursor-pointer border-[#EFEDE7] bg-white hover:border-[#1A4D2E]'}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <span className="block truncate text-[13px] font-semibold text-[#2C2C2C]">
                                        {school.name}
                                    </span>
                                    <span id={detailId} className="sr-only">{detail}</span>
                                    <span aria-hidden="true" className="mt-0.5 flex flex-wrap items-center gap-1">
                                        {/* No band, no chip — a default band
                                            would be a guess the student reads
                                            as a finding. */}
                                        {school.band && (
                                            <span data-testid="concierge-school-band"
                                                  className={`rounded-full border px-1.5 py-px text-[9.5px] font-semibold
                                                      ${chip || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                                {school.band}
                                            </span>
                                        )}
                                        <span className="font-mono text-[10px] tabular-nums text-[#9A968C]">
                                            {school.admit != null ? `${school.admit}%` : '—'} admit
                                            {school.state ? ` · ${school.state}` : ''}
                                        </span>
                                    </span>
                                    {school.majorNote && (
                                        <span aria-hidden="true"
                                              className={`mt-0.5 block truncate text-[10px] ${
                                                  school.majorImpacted ? 'text-amber-700' : 'text-[#9A968C]'}`}>
                                            {school.majorNote}
                                        </span>
                                    )}
                                </div>
                                <span aria-hidden="true"
                                      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border
                                          text-sm leading-none ${isSelected
                                              ? 'border-[#1A4D2E] bg-[#1A4D2E] text-white'
                                              : 'border-[#E0DED8] bg-white text-[#1A4D2E]'}`}>
                                    {isSelected ? <CheckIcon className="h-3.5 w-3.5" /> : '+'}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* #1199 (founder: "it should allow me to see more schools"). The
                old chip flow had a "Show me more options" escape hatch and the
                picker lost it, so a student who didn't like the visible eight
                could only type. These rows came from the SAME scored read —
                revealing them costs no turn and no wait. */}
            {hidden > 0 && (
                <button
                    onClick={() => setExpanded(true)}
                    data-testid="concierge-school-more"
                    className="mt-1.5 w-full rounded-lg border border-[#D6E8D5] bg-white px-3 py-1.5
                        text-[11px] font-semibold text-[#1A4D2E] hover:bg-[#F0F7F1]"
                >
                    Show me {hidden} more {hidden === 1 ? 'option' : 'options'}
                </button>
            )}

            <button
                onClick={submit}
                disabled={count === 0 || busy}
                data-testid="concierge-add-schools"
                className="mt-2 w-full rounded-lg bg-[#1A4D2E] px-3 py-2 text-xs font-semibold text-white
                    hover:bg-[#2D6B45] disabled:cursor-not-allowed disabled:opacity-40"
            >
                {count === 0
                    ? 'Add schools to my list'
                    : `Add ${count} school${count === 1 ? '' : 's'} to my list`}
            </button>
            {/* What happens next, stated plainly: the batch goes back to the
                counselor, which comes back with one card to confirm. */}
            <p className="mt-1.5 text-[10px] text-[#9A968C]">
                Nothing is added until you confirm on the next step.
            </p>
        </div>
    );
}
